import { Box, Skeleton, useTheme } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { BADGE, CONTROL_HEIGHT, NEUTRAL, SURFACE } from "@/theme/tokens";
import { SystemDataGrid } from "@/ui/Tables/SystemDataGrid";
import { EXPAND_COLUMN_RENDERED_WIDTH_PX } from "@/ui/Tables/SystemDataGrid/utils";
import { FACET_WIDTH, GRID_MIN_HEIGHT, SEARCH_SLOT_SX, TOOLBAR_LABELLED_ACTIONS_FROM, TOOLBAR_ONE_LINE_FROM } from "./consts";
import { type LocationsColumnShape, locationsColumnShapes } from "./gridColumnShape";
import { LocationsPageFrame } from "./LocationsPageFrame";
import { LocationsPageHeader } from "./LocationsPageHeader";
import { locationsGridSx } from "./spotlight/locationsGridSx";

/** Placeholder rows under the headers: about a screen's worth of a location's band and shifts. */
const SKELETON_ROWS = 8;
/** The toolbar's job chips while they load: a few, at a typical chip's width (px). */
const JOB_CHIP_WIDTHS = [72, 56, 64];
/**
 * The toolbar's buttons on the right (Collapse all, New location) at their labelled widths (px); between
 * `TOOLBAR_ONE_LINE_FROM` and `TOOLBAR_LABELLED_ACTIONS_FROM` they are icon buttons, as the toolbar's are.
 */
const TOOLBAR_BUTTON_WIDTHS = [112, 128];
/** The building strip's figures while they load (px): its title, then a job and its periods. */
const STRIP_BLOCK_WIDTHS = [96, 160, 160];

/** Holds the place of the grid's row-expand chevrons, at their width. */
const CHEVRON_COLUMN: LocationsColumnShape = {
	field: "__chevron",
	headerName: "",
	width: EXPAND_COLUMN_RENDERED_WIDTH_PX,
	sortable: false,
	hideHeaderSeparator: true,
};

/** The skeleton has no rows of its own: the grid draws its placeholder rows while `loading`. */
type SkeletonLine = { id: string };
const rows: SkeletonLine[] = [];
const getRowId = (line: SkeletonLine): string => line.id;

/**
 * The Locations page while its code loads (the route's Suspense fallback), in place of a spinner. Its real header and
 * section tabs (already in the main bundle) show at once; the toolbar and the building strip are placeholders in their
 * places, and the grid is `SystemDataGrid`'s own loading rows under the page's real column headers
 * (`locationsColumnShapes`), all in the page's own frame (`LocationsPageFrame`), so the page fills in rather than
 * jumping. It draws nothing from the page's own code, which is what it waits for.
 */
export const LocationsPageSkeleton: FC = () => {
	const { t } = useTranslation();
	const theme = useTheme();
	const { chosenDepartment } = useHospitalData();
	const isJobManaged = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const statsMode = chosenDepartment.statsMode;
	// The page's grid opens rows from a chevron column ahead of these, so the headers start where the page's do.
	const columns = useMemo(() => [CHEVRON_COLUMN, ...locationsColumnShapes(t, isJobManaged, statsMode, false)], [t, isJobManaged, statsMode]);
	const gridSx = useMemo(() => locationsGridSx(theme), [theme]);

	return (
		<LocationsPageFrame>
			<Box data-testid="locations-page-skeleton" aria-busy="true" sx={{ display: "contents" }}>
				<LocationsPageHeader showUnitFloorEditor={false} />
				<Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
					<Box sx={SEARCH_SLOT_SX}>
						<Skeleton variant="rounded" sx={{ height: CONTROL_HEIGHT.STANDARD, borderRadius: 1 }} />
					</Box>
					{JOB_CHIP_WIDTHS.map((width) => (
						<Skeleton key={width} variant="rounded" sx={{ width, height: CONTROL_HEIGHT.COMPACT, borderRadius: BADGE.PILL_RADIUS_FULL, flexShrink: 0 }} />
					))}
					<Skeleton variant="rounded" sx={{ width: FACET_WIDTH, height: CONTROL_HEIGHT.STANDARD, borderRadius: 1, flexShrink: 0 }} />
					<Box sx={{ display: "flex", gap: 1, marginInlineStart: "auto", flexShrink: 0 }}>
						{TOOLBAR_BUTTON_WIDTHS.map((width) => (
							<Skeleton
								key={width}
								variant="rounded"
								sx={{
									width: { xs: width, [TOOLBAR_ONE_LINE_FROM]: CONTROL_HEIGHT.COMPACT, [TOOLBAR_LABELLED_ACTIONS_FROM]: width },
									height: CONTROL_HEIGHT.COMPACT,
									borderRadius: 1,
								}}
							/>
						))}
					</Box>
				</Box>
				<Box
					sx={{
						mt: 1,
						display: "flex",
						alignItems: "center",
						columnGap: 1.75,
						backgroundColor: SURFACE,
						border: `1px solid ${NEUTRAL[200]}`,
						borderRadius: 1,
						px: 1.75,
						py: 1.25,
					}}
				>
					{STRIP_BLOCK_WIDTHS.map((width, index) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: fixed placeholders with no identity of their own.
						<Skeleton key={index} variant="text" sx={{ width }} />
					))}
				</Box>
				<Box sx={{ mt: 1.25, flex: 1, minHeight: GRID_MIN_HEIGHT, display: "flex", flexDirection: "column" }}>
					<SystemDataGrid<SkeletonLine>
						rows={rows}
						columns={columns}
						getRowId={getRowId}
						loading
						skeletonRowCount={SKELETON_ROWS}
						sx={gridSx}
						dataTestId="locations-grid-loading"
					/>
				</Box>
			</Box>
		</LocationsPageFrame>
	);
};
